import { Check } from 'lucide-react';

import { cn, formatDate } from '@/lib/utils';

import { HighlightedText } from './HighlightedText';
import { documentLabel } from './documentLabels';
import { RepositoryDocument } from './types';

interface Props {
    id: string;
    document: RepositoryDocument;
    query: string;
    active: boolean;
    added: boolean;
    onPick: () => void;
    onHover: () => void;
}

/** One row of the documents listbox. Added rows stay visible but cannot be picked again. */
export function DocumentOption({
    id,
    document,
    query,
    active,
    added,
    onPick,
    onHover,
}: Props) {
    const meta = [document.asset, formatDate(document.date)]
        .filter(Boolean)
        .join(' · ');
    return (
        <div
            id={id}
            role="option"
            aria-selected={active}
            aria-disabled={added}
            // Keeps focus in the search input so the list stays open after a pick.
            onMouseDown={(event) => event.preventDefault()}
            onClick={onPick}
            onMouseEnter={onHover}
            className={cn(
                'flex cursor-pointer flex-col gap-1 rounded-md px-3 py-2',
                active && 'bg-accent',
                added && 'cursor-default opacity-70',
            )}
        >
            <div className="flex min-w-0 flex-wrap items-baseline gap-x-3">
                <span className="break-all font-mono text-[13px] font-medium">
                    <HighlightedText
                        text={documentLabel(document)}
                        query={query}
                    />
                </span>
                <span className="ml-auto whitespace-nowrap font-mono text-xs text-muted-foreground">
                    {meta}
                </span>
            </div>
            <div className="flex min-w-0 items-center gap-2">
                <span className="min-w-0 truncate text-[13px] text-slate-600">
                    <span className="text-muted-foreground">
                        {document.typeLabel} ·{' '}
                    </span>
                    <HighlightedText
                        text={document.title ?? document.fileName}
                        query={query}
                    />
                </span>
                {added && (
                    <span className="ml-auto flex shrink-0 items-center gap-1 text-xs font-semibold text-primary">
                        <Check className="h-3 w-3" aria-hidden="true" />
                        Added
                    </span>
                )}
            </div>
        </div>
    );
}
